import React, { useState } from 'react';
import { Mail, Send, CheckCircle, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const Newsletter = ({ isDarkMode }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append("access_key", "e8bd8702-cdd8-408b-919f-f6ac1538249a");
      formData.append("email", email);
      formData.append("name", name || "Newsletter Subscriber");
      formData.append("subject", "New Newsletter Subscription - Pathways Africa");
      formData.append("message", `New newsletter subscription from ${email}`);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setEmail('');
        setName('');
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to subscribe. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="newsletter" className={`py-28 relative overflow-hidden ${isDarkMode ? 'bg-[#1a2625]' : 'bg-[#E5D9B6]'}`}>
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#E5D9B6]/8' : 'bg-[#284139]/10'}`} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Content Card */}
        <div className="max-w-3xl mx-auto text-center">
          {/* Icon with glow effect */}
          <div className="relative inline-block mb-8">
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center animate-pulse-glow ${isDarkMode ? 'bg-gradient-to-br from-[#E5D9B6]/20 to-[#284139]' : 'bg-gradient-to-br from-[#284139]/20 to-[#284139]'}`}>
              <Mail className={`w-9 h-9 ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#E5D9B6]'}`} />
            </div>
            <Sparkles className={`absolute -top-2 -right-2 w-6 h-6 animate-pulse ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}`} />
          </div>

          {/* Header */}
          <h2 className={`font-display text-4xl lg:text-5xl font-bold mb-5 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#284139]'}`}>
            Stay Updated
          </h2>
          <p className={`text-lg mb-10 max-w-xl mx-auto ${isDarkMode ? 'text-[#809076]' : 'text-[#284139]/70'}`}>
            Subscribe to our newsletter for the latest updates on events, webinars, forums, programs, application deadlines and success stories from our scholars.
          </p>

          {/* Form with glass effect */}
          <form onSubmit={handleSubmit} className="relative">
            <div className={`flex flex-col gap-4 max-w-xl mx-auto p-4 rounded-2xl backdrop-blur-sm ${isDarkMode ? 'bg-[#111A19]/60 border border-[#284139]' : 'bg-[#284139] border border-[#284139]'}`}>
              {/* Name Input */}
              <div className="relative">
                <Input
                  type="text"
                  name="name"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full h-14 pl-5 pr-12 bg-transparent border-0 rounded-xl focus:ring-2 focus:ring-[#E5D9B6]/30 transition-all ${isDarkMode ? 'text-[#f5f5f5] placeholder:text-[#809076]/60' : 'text-[#E5D9B6] placeholder:text-[#E5D9B6]/50'}`}
                  disabled={isLoading}
                />
              </div>
              
              {/* Email Input and Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Input
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full h-14 pl-5 pr-12 bg-transparent border-0 rounded-xl focus:ring-2 focus:ring-[#E5D9B6]/30 transition-all ${isDarkMode ? 'text-[#f5f5f5] placeholder:text-[#809076]/60' : 'text-[#E5D9B6] placeholder:text-[#E5D9B6]/50'}`}
                    disabled={isLoading}
                    required
                  />
                  <Mail className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-[#809076]/50' : 'text-[#E5D9B6]/50'}`} />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading || !email}
                  className={`h-14 px-8 font-semibold rounded-xl btn-transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg ${
                    isDarkMode 
                      ? 'bg-[#E5D9B6] hover:bg-[#d4c9a5] text-[#111A19] shadow-[#E5D9B6]/20'
                      : 'bg-[#E5D9B6] hover:bg-[#d4c9a5] text-[#284139] shadow-[#E5D9B6]/20'
                  }`}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-[#111A19]/30 border-t-[#111A19] rounded-full animate-spin" />
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle size={18} />
                      Subscribed!
                    </>
                  ) : (
                    <>
                      Subscribe
                      <Send size={18} />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>

          {/* Success Message */}
          {isSubmitted && (
            <p className={`mt-5 text-sm animate-fade-in font-medium ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}`}>
              Thank you for subscribing! We'll keep you updated.
            </p>
          )}

          {/* Error Message */}
          {error && (
            <p className="mt-5 text-red-400 text-sm animate-fade-in font-medium flex items-center justify-center gap-2">
              <AlertCircle size={16} />
              {error}
            </p>
          )}

          {/* Privacy Note */}
          <p className={`mt-8 text-xs ${isDarkMode ? 'text-[#809076]/60' : 'text-[#284139]/60'}`}>
            By subscribing, you agree to receive occasional emails from Pathways
            Africa. We respect your privacy and will never share your information.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
